/**
 * Custom hook for family management event handlers
 *
 * Extracted from family-manage.tsx to separate business logic from presentation.
 * Handles:
 * - Invite member (email validation, analytics, Edge Function, share logic)
 * - Remove member (member lookup, confirmation, analytics)
 * - Cancel invite (date calc, confirmation, analytics)
 *
 * Pattern: Container/Presentational separation
 * - This hook = business logic
 * - Components = presentation
 */
import { useState } from 'react';
import { Alert, Share, Platform } from 'react-native';
import type { TFunction } from '@dramadetector/shared';
import { useTranslation } from '@dramadetector/shared';
import { validateAndSanitizeEmail, isFreeEmailProvider } from '@dramadetector/shared';
import { trackEvent } from '../../../lib/analytics/events';

interface FamilyPlan {
  id: string;
  tier: 'drama_plus' | 'dramax';
  billing_interval: string;
  daily_quota: number;
  max_members: number;
  member_count: number;
}

interface FamilyMember {
  user_id: string;
  email: string;
  display_name: string | null;
  is_admin: boolean;
  joined_at: string;
}

interface PendingInvite {
  id: string;
  invited_email: string;
  created_at: string;
  expires_at: string;
  deep_link: string;
}

interface UseFamilyManageHandlersProps {
  familyPlan: FamilyPlan | null;
  members: FamilyMember[];
  inviteMember: (email: string) => Promise<PendingInvite>;
  // Wave 4B: underlying mutations now return typed DTOs; we don't read them
  // here, but accept `unknown` so callers can pass the validated shape.
  removeMember: (userId: string) => Promise<unknown>;
  cancelInvite: (inviteId: string) => Promise<unknown>;
  t: TFunction<"subscription">;
}

export function useFamilyManageHandlers({
  familyPlan,
  members,
  inviteMember,
  removeMember,
  cancelInvite,
  t,
}: UseFamilyManageHandlersProps) {
  const { t: tCommon } = useTranslation('common');
  const [inviteEmail, setInviteEmail] = useState('');
  const [showInviteInput, setShowInviteInput] = useState(false);

  /**
   * Handle invite member
   *
   * Flow:
   * 1. Validate and sanitize email (shared utility)
   * 2. Track button click event (with privacy-safe domain type)
   * 3. Call inviteMember mutation (Edge Function)
   * 4. Validate deep link format
   * 5. Share invitation link (native Share API or clipboard fallback)
   * 6. Reset form state
   * 7. Handle errors with analytics
   */
  const handleInviteMember = async () => {
    // Validate and sanitize email using shared utility
    const { valid, sanitized, error } = validateAndSanitizeEmail(inviteEmail);

    if (!valid) {
      Alert.alert(t('family.error'), error || t('family.emailInvalid'));
      return;
    }

    try {
      // Track with generic domain classification (free vs custom)
      const domainType = isFreeEmailProvider(sanitized) ? 'free' : 'custom';

      trackEvent('Button_Click_InviteFamilyMember', {
        invited_email_domain_type: domainType,
        invite_method: 'manual_input',
        current_member_count: members.length,
        max_members: 5,
      });

      const invite = await inviteMember(sanitized);

      // Validate deep link before sharing
      if (!invite.deep_link || !invite.deep_link.startsWith('dramadetector://')) {
        throw new Error('Invalid invitation link');
      }

      // Share invite link
      if (Platform.OS !== 'web') {
        await Share.share({
          message: t('family.inviteMessage', {
            link: invite.deep_link,
            tier: familyPlan?.tier === 'dramax' ? 'DramaX' : 'Drama+',
          }),
          url: invite.deep_link,
        });
      } else {
        // Copy to clipboard on web
        Alert.alert(
          t('family.inviteSent'),
          t('family.inviteLinkCopied', { email: sanitized })
        );
      }

      setInviteEmail('');
      setShowInviteInput(false);
    } catch (error) {
      trackEvent('Error_InviteFamilyMember', {
        error_type: 'unknown',
        error_message: error instanceof Error ? error.message : 'Unknown',
      });

      Alert.alert(t('family.error'), error instanceof Error ? error.message : t('family.inviteFailed'));
    }
  };

  /**
   * Handle remove member
   *
   * Flow:
   * 1. Find member to calculate joined_days_ago
   * 2. Show confirmation alert
   * 3. Track button click event (with member metadata)
   * 4. Call removeMember mutation (Edge Function)
   * 5. Show success message
   * 6. Handle errors with analytics
   *
   * @param userId - User ID to remove
   * @param displayName - Display name for confirmation message
   */
  const handleRemoveMember = (userId: string, displayName: string) => {
    // Find member to calculate joined_days_ago
    const member = members.find((m) => m.user_id === userId);
    const memberJoinedDaysAgo = member
      ? Math.floor((Date.now() - new Date(member.joined_at).getTime()) / (1000 * 60 * 60 * 24))
      : 0;

    Alert.alert(
      t('family.confirmRemove'),
      t('family.confirmRemoveMessage', { name: displayName }),
      [
        { text: tCommon('cancel'), style: 'cancel' },
        {
          text: tCommon('remove'),
          style: 'destructive',
          onPress: async () => {
            try {
              trackEvent('Button_Click_RemoveFamilyMember', {
                member_user_id: userId,
                member_joined_days_ago: memberJoinedDaysAgo,
                remaining_members: members.length - 1,
              });

              await removeMember(userId);

              Alert.alert(t('family.success'), t('family.memberRemoved'));
            } catch (error) {
              trackEvent('Error_RemoveFamilyMember', {
                error_type: 'unknown',
                error_message: error instanceof Error ? error.message : 'Unknown',
              });

              Alert.alert(
                t('family.error'),
                error instanceof Error ? error.message : t('family.removeFailed')
              );
            }
          },
        },
      ]
    );
  };

  /**
   * Handle cancel invite
   *
   * Flow:
   * 1. Calculate invite age in hours
   * 2. Determine email domain type (privacy-first)
   * 3. Show confirmation alert
   * 4. Track button click event (with privacy-safe metadata)
   * 5. Call cancelInvite mutation (Edge Function)
   * 6. Show success message
   * 7. Handle errors
   *
   * @param inviteId - Invitation ID to cancel
   * @param email - Email address for confirmation message
   * @param createdAt - Invitation creation timestamp
   */
  const handleCancelInvite = (inviteId: string, email: string, createdAt: string) => {
    // Calculate invite age in hours
    const inviteAgeHours = Math.floor(
      (Date.now() - new Date(createdAt).getTime()) / (1000 * 60 * 60)
    );

    // Determine email domain type (privacy-first)
    const domainType = isFreeEmailProvider(email) ? 'free' : 'custom';

    Alert.alert(
      t('family.confirmCancelInvite'),
      t('family.confirmCancelInviteMessage', { email }),
      [
        { text: tCommon('cancel'), style: 'cancel' },
        {
          text: tCommon('cancelInvite'),
          style: 'destructive',
          onPress: async () => {
            try {
              trackEvent('Button_Click_CancelFamilyInvite', {
                invite_age_hours: inviteAgeHours,
                invite_email_domain_type: domainType,
              });

              await cancelInvite(inviteId);

              Alert.alert(t('family.success'), t('family.inviteCancelled'));
            } catch (error) {
              Alert.alert(
                t('family.error'),
                error instanceof Error ? error.message : t('family.cancelInviteFailed')
              );
            }
          },
        },
      ]
    );
  };

  return {
    inviteEmail,
    setInviteEmail,
    showInviteInput,
    setShowInviteInput,
    handleInviteMember,
    handleRemoveMember,
    handleCancelInvite,
  };
}
